import { toast } from '@westhetics/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useRealtime } from '@/lib/realtime'
import { useSession } from '@/lib/session'
import { invalidateSignatures } from './api'
import { documentsWord, firstName } from './labels'

/**
 * O que a paciente faz chega na hora (RN-ASS-32, T39): a lista, o painel, a ficha e o dashboard
 * recarregam a cada evento do envio da clínica ativa, e o aviso diz quem assinou, quem abriu o link e
 * quem não concordou. Quem está no balcão esperando não precisa recarregar nem olhar o e-mail.
 */

export interface SignatureLiveEvent {
  type: string
  signatureRequestId: string
  detail: string | null
  data: {
    appointmentId?: string
    patientName?: string
    documentCount?: number
    isAwaitingClinicSigner?: boolean
    reason?: string
  }
}

/** A frase do aviso para um evento; `null` quando o evento só atualiza a tela. */
export function liveSentence(event: SignatureLiveEvent): string | null {
  const name = event.data.patientName ? firstName(event.data.patientName) : 'A paciente'
  const docs = documentsWord(event.data.documentCount ?? 0)
  switch (event.type) {
    case 'signed':
      return `${name} assinou ${docs}${event.data.isAwaitingClinicSigner ? ' · falta a responsável assinar' : ''}`
    case 'declined':
      return `${name} não concordou com os documentos`
    case 'link_opened':
      return `${name} abriu o link agora`
    case 'expired':
      return `O link de ${name} venceu`
    default:
      return null
  }
}

export function toLiveEvent(row: Record<string, unknown>): SignatureLiveEvent {
  return {
    type: String(row.type ?? ''),
    signatureRequestId: String(row.signature_request_id ?? ''),
    detail: typeof row.detail === 'string' ? row.detail : null,
    data: (row.data && typeof row.data === 'object' ? row.data : {}) as SignatureLiveEvent['data'],
  }
}

export function useSignatureLive(options: { onEvent?: (event: SignatureLiveEvent) => void } = {}) {
  const realtime = useRealtime()
  const session = useSession()
  const queryClient = useQueryClient()
  const clinicId = session.profile?.clinicId ?? null
  const { onEvent } = options

  useEffect(() => {
    if (!realtime || !clinicId) return
    return realtime.onInsert('signature_request_events', `clinic_id=eq.${clinicId}`, (row) => {
      const event = toLiveEvent(row)
      void invalidateSignatures(queryClient)
      const sentence = liveSentence(event)
      if (sentence) toast(sentence)
      onEvent?.(event)
    })
  }, [realtime, clinicId, queryClient, onEvent])
}
